import { trpc } from '@/utils/trpc';
import { SubjectTableData, SubjectFormData } from '../types';
import { notificationService } from '@shared/components/notifications';

/**
 * Helper function to check if error should be retried
 * Don't retry on 4xx client errors, but retry on server errors
 */
function shouldRetryError(failureCount: number, error: unknown): boolean {
  const httpStatus =
    error && typeof error === 'object' && 'data' in error
      ? (error.data as { httpStatus?: number } | null | undefined)?.httpStatus
      : undefined;

  // Don't retry on 4xx errors
  if (httpStatus && httpStatus >= 400 && httpStatus < 500) {
    return false;
  }

  // Retry up to 3 times for other errors
  return failureCount < 3;
}

/**
 * Query hook for fetching subjects by domain
 * Uses tRPC for type-safe API calls
 *
 * @param tenantId - UUID of the parent tenant (required for DynamoDB key lookup)
 * @param domainId - UUID of the domain to fetch subjects for
 */
export const useSubjects = (tenantId: string, domainId: string) => {
  const query = trpc.subject.listByDomain.useQuery(
    { tenantId, domainId, limit: 100 },
    {
      enabled: !!tenantId && !!domainId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: shouldRetryError,
    }
  );

  // Transform tRPC response to table data format
  // Note: Maps subjectId → id for backward compatibility with existing UI components
  // that expect SubjectTableData.id (used in DataManagementTab and other shared components)
  const tableData: SubjectTableData[] = (query.data?.items || []).map((subject) => ({
    id: subject.subjectId,
    tenantId: subject.tenantId,
    domainId: subject.domainId,
    name: subject.name,
    code: subject.code,
    description: subject.description,
    enrollmentCount: subject.enrollmentCount,
    createdAt: subject.createdAt,
  }));

  return {
    data: tableData,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};

/**
 * Query hook for fetching a single subject by ID
 * Uses tRPC for type-safe API calls
 *
 * @param tenantId - UUID of the parent tenant (required for DynamoDB key lookup)
 * @param subjectId - UUID of the subject
 */
export const useSubject = (tenantId: string, subjectId: string) => {
  return trpc.subject.getById.useQuery(
    { tenantId, subjectId },
    {
      enabled: !!tenantId && !!subjectId,
      staleTime: 5 * 60 * 1000,
      retry: shouldRetryError,
    }
  );
};

/**
 * Mutation hook for creating a subject
 * Uses tRPC for type-safe API calls with automatic cache invalidation
 *
 * @param tenantId - UUID of the parent tenant
 * @param domainId - UUID of the parent domain
 */
export const useCreateSubject = (tenantId: string, domainId: string) => {
  const utils = trpc.useUtils();

  const mutation = trpc.subject.create.useMutation({
    onSuccess: (newSubject) => {
      // Invalidate the list query for this domain to refetch
      utils.subject.listByDomain.invalidate({ tenantId, domainId });
      utils.subject.list.invalidate({ tenantId });

      if (newSubject) {
        notificationService.success(`Subject "${newSubject.name}" created successfully`);
      }
    },
    onError: (error) => {
      const errorMessage = error?.message || 'Failed to create subject';
      notificationService.error(errorMessage);
    },
  });

  // Wrapper to maintain backward compatibility with existing component interface
  // Components call: mutate({ name, code, description?, enrollmentCount? })
  // tRPC expects: mutate({ tenantId, domainId, name, code, description?, enrollmentCount? })
  return {
    ...mutation,
    mutate: (variables: SubjectFormData) => {
      mutation.mutate({
        tenantId,
        domainId,
        ...variables,
      });
    },
    mutateAsync: async (variables: SubjectFormData) => {
      return mutation.mutateAsync({
        tenantId,
        domainId,
        ...variables,
      });
    },
  };
};

/**
 * Mutation hook for updating a subject
 * Uses tRPC for type-safe API calls with automatic cache invalidation
 *
 * @param tenantId - UUID of the parent tenant
 * @param domainId - UUID of the parent domain
 */
export const useUpdateSubject = (tenantId: string, domainId: string) => {
  const utils = trpc.useUtils();

  const mutation = trpc.subject.update.useMutation({
    onSuccess: (updatedSubject) => {
      if (updatedSubject) {
        // Invalidate the specific subject and list queries
        utils.subject.getById.invalidate({
          tenantId: updatedSubject.tenantId,
          subjectId: updatedSubject.subjectId,
        });
        utils.subject.listByDomain.invalidate({ tenantId, domainId });
        utils.subject.list.invalidate({ tenantId });

        notificationService.success(`Subject "${updatedSubject.name}" updated successfully`);
      }
    },
    onError: (error) => {
      const errorMessage = error?.message || 'Failed to update subject';
      notificationService.error(errorMessage);
    },
  });

  // Wrapper to maintain backward compatibility with existing component interface
  // Components call: mutate({ id, data: { name?, code?, description?, enrollmentCount? } })
  // tRPC expects: mutate({ tenantId, id, name?, code?, description?, enrollmentCount? })
  return {
    ...mutation,
    mutate: (variables: { id: string; data: SubjectFormData }) => {
      mutation.mutate({
        tenantId,
        id: variables.id,
        ...variables.data,
      });
    },
    mutateAsync: async (variables: { id: string; data: SubjectFormData }) => {
      return mutation.mutateAsync({
        tenantId,
        id: variables.id,
        ...variables.data,
      });
    },
  };
};

/**
 * Mutation hook for deleting a subject
 * Uses tRPC for type-safe API calls with automatic cache invalidation
 *
 * @param tenantId - UUID of the parent tenant
 * @param domainId - UUID of the parent domain
 */
export const useDeleteSubject = (tenantId: string, domainId: string) => {
  const utils = trpc.useUtils();

  const mutation = trpc.subject.delete.useMutation({
    onSuccess: (_result, variables) => {
      // Invalidate cache queries
      utils.subject.getById.invalidate({
        tenantId: variables.tenantId,
        subjectId: variables.id,
      });
      utils.subject.listByDomain.invalidate({ tenantId, domainId });
      utils.subject.list.invalidate({ tenantId });

      notificationService.success('Subject deleted successfully');
    },
    onError: (error) => {
      const errorMessage = error?.message || 'Failed to delete subject';
      notificationService.error(errorMessage);
    },
  });

  // Wrapper to maintain backward compatibility with existing component interface
  // Components pass: subjectId (string)
  // tRPC expects: { tenantId, id: subjectId }
  return {
    ...mutation,
    mutate: (subjectId: string) => {
      mutation.mutate({ tenantId, id: subjectId });
    },
    mutateAsync: async (subjectId: string) => {
      return mutation.mutateAsync({ tenantId, id: subjectId });
    },
  };
};

/**
 * Query hook for fetching domain info
 * Still uses tRPC domain router
 *
 * @param tenantId - UUID of the parent tenant
 * @param domainId - UUID of the domain
 */
export const useDomainInfo = (tenantId: string, domainId: string) => {
  return trpc.domain.getById.useQuery(
    { tenantId, domainId },
    {
      enabled: !!tenantId && !!domainId,
      staleTime: 5 * 60 * 1000,
      retry: shouldRetryError,
    }
  );
};
